import { useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Logo } from "../components/Logo";
import { ThemeToggle } from "../components/ThemeToggle";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight, Eye, EyeOff, KeyRound, ShieldCheck } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ResetPasswordPage = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") || "";
  const navigate = useNavigate();

  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password !== confirm) {
      toast.error("Passwords don't match");
      return;
    }
    if (password.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/auth/reset-password`, { token, new_password: password });
      setDone(true);
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-ink-50 flex flex-col px-6 py-10">
      <div className="flex items-center justify-between">
        <Link
          to="/login"
          className="inline-flex items-center gap-2 text-sm font-medium text-ink-500 hover:text-ink-950 transition-colors w-fit"
          data-testid="back-to-login"
        >
          <ArrowLeft className="w-4 h-4" /> Back to sign in
        </Link>
        <ThemeToggle />
      </div>

      <div className="flex-1 flex flex-col justify-center max-w-sm w-full mx-auto">
        <Logo size={40} tagline animated linkTo={null} className="mb-10" />

        {done ? (
          <div data-testid="reset-success-message">
            <div className="w-14 h-14 rounded-2xl bg-coral-50 flex items-center justify-center mb-6">
              <ShieldCheck className="w-7 h-7 text-coral-500" />
            </div>
            <h1 className="font-display font-black text-3xl tracking-[-0.04em] leading-tight">
              Password updated.
            </h1>
            <p className="text-ink-500 mt-3 font-light">
              Your password has been reset. Sign in with your new password to continue.
            </p>
            <Button
              size="lg"
              className="w-full mt-8"
              onClick={() => navigate("/login")}
              data-testid="reset-go-login-btn"
            >
              Sign in <ArrowRight className="w-4 h-4" />
            </Button>
          </div>
        ) : !token ? (
          <div data-testid="reset-invalid-message">
            <div className="w-14 h-14 rounded-2xl bg-ink-100 flex items-center justify-center mb-6">
              <KeyRound className="w-7 h-7 text-ink-500" />
            </div>
            <h1 className="font-display font-black text-3xl tracking-[-0.04em] leading-tight">
              Invalid reset link.
            </h1>
            <p className="text-ink-500 mt-3 font-light">
              This link is missing its reset token. Request a fresh one and try again.
            </p>
            <Link to="/forgot-password">
              <Button size="lg" className="w-full mt-8" data-testid="reset-request-new-btn">
                Request a new link
              </Button>
            </Link>
          </div>
        ) : (
          <>
            <h1 className="font-display font-black text-4xl tracking-[-0.04em] leading-tight">
              Choose a new password.
            </h1>
            <p className="text-ink-500 mt-2 font-light">
              Make it at least 6 characters — something you'll remember this time. 😉
            </p>

            <form onSubmit={handleSubmit} className="space-y-5 mt-10">
              <div className="space-y-2">
                <Label htmlFor="password" className="text-xs font-bold uppercase tracking-[0.18em] text-ink-500">
                  New password
                </Label>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••••"
                    required
                    className="pr-12"
                    data-testid="reset-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-ink-500 hover:text-ink-950"
                    aria-label="toggle password visibility"
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="confirm" className="text-xs font-bold uppercase tracking-[0.18em] text-ink-500">
                  Confirm password
                </Label>
                <Input
                  id="confirm"
                  type={showPassword ? "text" : "password"}
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  placeholder="••••••••"
                  required
                  data-testid="reset-confirm-input"
                />
              </div>

              <Button
                type="submit"
                disabled={loading}
                size="lg"
                className="w-full"
                data-testid="reset-submit-btn"
              >
                {loading ? "Updating..." : (<>Reset password <ArrowRight className="w-4 h-4" /></>)}
              </Button>
            </form>
          </>
        )}
      </div>
    </div>
  );
};

export default ResetPasswordPage;
